<template lang="pug">
.content-center.slot-game
  .overlay
  .back-btn(@click="go('/lobby')")
    i.fa-solid.fa-left-long
    |  {{ $t("ui.back") }}
  .slot-banner
    img.img-fluid(src='images/slot/slot-banner.jpg')
  .slot-thumb-wrapper
    .scrolling-wrapper
      .slot-thumb(
        v-for="(slot, index) in slotItems"
        :key="`slot-${slot.mode}`"
        :ref="`slot-thumb-${slot.mode}`"
        :class="{ active: isSlotActive(slot) }" 
        @click="selectGameProvider(slot)"
      )
        img(:src='getImgUrl(slot.icon)' :alt="$t(slot.textKey)")
  .slot-title
    | {{ selectedGameProviderText }}
  .loading-slot(v-if="isLoadingGameList")
    .stack
      .stack_card
      .stack_card
      .stack_card
    div {{ $t('ui.loading') }}
  .loading-fail(v-else-if="!slotGameList.length")
    img.icon(src='images/maintenance-icon.svg' alt='')
    div {{ $t('error.game_maintenance') }}
  template(v-else)
    .game-list-wrapper(v-if="hasActiveGameList")
      .game-item(
        v-for="(slot, index) in slotGameList" 
        :key="getGameItemKey(slot, index)"
        role="button"
        @click="launchGame(slot)"
      )
        img.game-thumb(
          v-if="getGameThumbnail(slot)"
          :src="getGameThumbnail(slot)"
          :alt="getGameName(slot)"
        )
        span.game-name {{ getGameName(slot) }}
</template>

<script>
import config from "@/config";
import mixinMain from "@/library/mixinMain";
import mixinProvider from "@/library/mixinProvider";
import xhr from "@/library/_xhr-game.js";

const DIRECT_LAUNCH_CODES = new Set(["PGS", "SP", "NEXT", "DB", "WOW"]);

const PROVIDER_BEHAVIORS = {
  EPW: {
    listMethod: "getEpwGameList",
    launchMethod: "launchEpw",
    gameCodeKey: "GameId",
    imageKey: "ImageUrl",
    nameKey: "GameName",
    transformList(games, context = {}) {
      if (!Array.isArray(games)) {
        return [];
      }
      const slotContext = context.slot || {};
      const targetGameType = slotContext.gameType || 1;
      // Filter EPW games by the slot's configured game type to avoid mixing lists.
      return games.filter(game => Number(game.GameType) === Number(targetGameType));
    }
  },
  WF: {
    listMethod: "getWfGameList",
    launchMethod: "launchWf",
    gameCodeKey: "GameId",
    imageKey: "ImageUrl",
    nameKey: "GameName"
  },
  OSG: {
    listMethod: "getLive22GameList",
    launchMethod: "launchLive22",
    gameCodeKey: "GameId",
    imageKey: "ImageUrl",
    nameKey: "GameName"
  },
  PP: {
    listMethod: "getPragmaticGameList",
    launchMethod: "launchPragmatic",
    gameCodeKey: "gameID",
    getImage(game) {
      return game.gameID ? `${config.ppgGameImgUrl}${game.gameID}/${game.gameID}_200x200_NB.png` : "";
    },
    getName(game) {
      return game.gameName || game.gameID || "";
    }
  },
  JK: {
    listMethod: "getJokerGameList",
    launchMethod: "launchJoker",
    gameCodeKey: "GameCode",
    getImage(game) {
      return game.Image1 ? `https:${game.Image1}` : "";
    },
    getName(game) {
      return game.GameName || game.GameCode || "";
    }
  },
  JILI: {
    listMethod: "getJiliGameList",
    launchMethod: "launchJili",
    gameCodeKey: "GameId",
    getImage(game) {
      return game.GameId ? `${config.jiliGameImgUrl}${game.GameId}.jpg` : "";
    },
    nameKey: "name"
  },
  UU: {
    listMethod: "getUuslGameList",
    launchMethod: "launchUusl",
    gameCodeKey: "GameId",
    imageKey: "ImageUrl",
    nameKey: "GameName"
  }
};

export default {
  mixins: [mixinMain, mixinProvider],
  data() {
    return {
      slotGameList: [],
      selectedGameProviderText: '',
      selectedGameProviderCode: '',
      selectedGameProviderMode: null,
      hasActiveGameList: false,
      isLoadingGameList: false,
      isLaunchingGame: false
    };
  },
  computed: {
    currencyCode() {
      return this.$store.getters.currencyCode;
    },
    mmoMode() {
      return this.$store.getters.mmoMode;
    },
    currentProviderConfig() {
      return this.getProviderConfig(this.selectedGameProviderCode);
    }
  },
  created() {
    const mode = this.$route.query.mode;
    if (mode) {
      const slot = this.slotItems.find(s => String(s.mode) === String(mode));
      if (slot) {
        this.selectGameProvider(slot);
        return;
      }
    }
    this.selectDefaultSlot();
  },
  methods: {
    go(e) {
      this.$router.push(e).catch(err => {});
      $('body').removeClass('open-side-nav');
    },
    getImgUrl(e) {
      return config.resourceImgUrl + '/mobile/lobby-thumb/new/' + e;
    },
    openSlotWindow(mode) {
      const href = `/slots?mode=${mode}`;
      window.open(href, '_blank', 'status=no,toolbar=no,menubar=no,location=no');
    },
    selectDefaultSlot() {
      const defaultSlot = this.slotItems.length ? this.slotItems[0] : null;
      if (defaultSlot) {
        this.selectGameProvider(defaultSlot);
      }
    },
    async selectGameProvider(slot) {
      this.selectedGameProviderText = this.$t(slot.textKey);
      this.selectedGameProviderCode = slot.code;
      this.selectedGameProviderMode = slot.mode;

      await this.handleProviderSelection(slot);
      
      this.$nextTick(() => {
        this.scrollToSelectedProvider(slot.mode);
      });
    },
    scrollToSelectedProvider(mode) {
      const ref = this.$refs[`slot-thumb-${mode}`];
      if (ref && ref[0]) {
        const element = ref[0];
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    },
    async handleProviderSelection(slot) {
      const providerConfig = this.getProviderConfig(slot.code);
      this.hasActiveGameList = Boolean(providerConfig);

      if (!this.hasActiveGameList || DIRECT_LAUNCH_CODES.has(slot.code)) {
        this.slotGameList = [];
        this.openSlotWindow(slot.mode);
        return;
      }

      await this.loadGameList(slot.code, providerConfig, slot);
    },
    getProviderConfig(code) {
      return PROVIDER_BEHAVIORS[code] || null;
    },
    buildGameListArgs() {
      return {
        username: this.$store.getters.accountId,
        sessionid: this.$store.getters.sessionToken,
        currency: this.currencyCode,
        lang: this.$store.getters.language
      };
    },
    async loadGameList(code, providerConfig = null, slotContext = null) {
      const targetConfig = providerConfig || this.getProviderConfig(code);
      if (!targetConfig) {
        this.slotGameList = [];
        return;
      }

      const loaderFnName = this.resolveXhrMethod(targetConfig.listMethod);
      if (!loaderFnName) {
        console.error('Game list loader not found for provider', code);
        this.slotGameList = [];
        return;
      }

      this.slotGameList = [];
      this.isLoadingGameList = true;
      try {
        const response = await xhr[loaderFnName](this.buildGameListArgs());
        if (response && response.success && Array.isArray(response.data)) {
          const processedList = typeof targetConfig.transformList === 'function'
            ? targetConfig.transformList(response.data, { slot: slotContext, code })
            : response.data;
          this.slotGameList = processedList || [];
        } else {
          this.slotGameList = [];
        }
      } catch (error) {
        this.slotGameList = [];
        console.error('Failed to load slot game list:', error);
      } finally {
        this.isLoadingGameList = false;
      }
    },
    resolveXhrMethod(methodName) {
      if (!methodName) {
        return null;
      }
      if (typeof xhr[methodName] === 'function') {
        return methodName;
      }
      const fallback = methodName.charAt(0).toLowerCase() + methodName.slice(1);
      return typeof xhr[fallback] === 'function' ? fallback : null;
    },
    isSlotActive(slot) {
      if (!slot) {
        return false;
      }
      return this.selectedGameProviderCode === slot.code && this.selectedGameProviderMode === slot.mode;
    },
    resolveGameCode(game, providerConfig) {
      if (!game || !providerConfig) {
        return null;
      }
      if (typeof providerConfig.getGameCode === 'function') {
        return providerConfig.getGameCode(game);
      }
      if (providerConfig.gameCodeKey) {
        return game[providerConfig.gameCodeKey];
      }
      return null;
    },
    buildLaunchArgs(gameCode) {
      return {
        username: this.$store.getters.accountId,
        sessionid: this.$store.getters.sessionToken,
        currency: this.currencyCode,
        lang: this.$store.getters.language,
        game_code: gameCode,
        mobile: 'true'
      };
    },
    openGameResponse(payload) {
      if (!payload) {
        return;
      }
      if (typeof payload === 'string' && (payload.includes('<html') || payload.includes('<!doctype'))) {
        const popup = window.open('', '_blank');
        if (popup) {
          popup.document.write(payload);
          popup.document.close();
        }
        return;
      }
      window.open(payload, '_blank', 'status=no,toolbar=no,menubar=no,location=no');
    },
    handleLaunchFailure(response) {
      const status = (response && response.status) || 'game_maintenance';
      console.error('Failed to launch slot game:', status);
      this.showLaunchError(status);      
    },
    showLaunchError(status) {
      if (this.$helpers && typeof this.$helpers.handleFeedback === 'function') {
        this.$helpers.handleFeedback(status || 'game_maintenance');
      } else {
        alert(this.$t(`error.${status}`) || this.$t('error.game_maintenance'));
      }
    },
    async launchGame(game) {
      if (!this.hasActiveGameList || this.isLaunchingGame) {
        return;
      }

      const providerConfig = this.currentProviderConfig;
      if (!providerConfig) {
        return;
      }

      const launchFnName = this.resolveXhrMethod(providerConfig.launchMethod || `launch${this.selectedGameProviderCode}`);
      if (!launchFnName) {
        console.error('launchGame function not found', providerConfig.launchMethod, this.selectedGameProviderCode);
        return;
      }

      const gameCode = this.resolveGameCode(game, providerConfig);
      // console.log('gameCode', gameCode);
      if (!gameCode) {
        console.error('Unable to resolve game code for provider', this.selectedGameProviderCode);
        return;
      }

      this.isLaunchingGame = true;
      try {
        const response = await xhr[launchFnName](this.buildLaunchArgs(gameCode));
        // console.log('response', response);
        if (response && response.success && response.data) {
          this.openGameResponse(response.data);
        } else {
          this.handleLaunchFailure(response);
        }
      } catch (error) {
        // console.error('launchGame error', error);
        this.showLaunchError(error && error.status);
      } finally {
        this.isLaunchingGame = false;
      }
    },
    getGameThumbnail(game) {
      const providerConfig = this.currentProviderConfig;
      if (!providerConfig || !game) {
        return '';
      }
      if (typeof providerConfig.getImage === 'function') {
        return providerConfig.getImage(game);
      }
      if (providerConfig.imageKey && game[providerConfig.imageKey]) {
        return game[providerConfig.imageKey];
      }
      return '';
    },
    getGameName(game) {
      const providerConfig = this.currentProviderConfig;
      if (!game) {
        return '';
      }
      if (providerConfig) {
        if (typeof providerConfig.getName === 'function') {
          return providerConfig.getName(game) || '';
        }
        if (providerConfig.nameKey && game[providerConfig.nameKey]) {
          const nameValue = game[providerConfig.nameKey];
          if (typeof nameValue === 'object' && nameValue !== null && !Array.isArray(nameValue)) {
            return nameValue['en-US'] || '';
          }
          return nameValue;
        }
      }
      return game.GameName || game.GameCode || game.gameID || '';
    },
    getGameItemKey(game, index) {
      const gameCode = this.resolveGameCode(game, this.currentProviderConfig);
      return gameCode ? `slot-game-${gameCode}` : `slot-game-${index}`;
    }
  }
};
</script>
